import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
  withGoogleMapsState,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import MapPinMarker from '@atom/components/common/map/markers/MapPinMarker';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import { getLoggedInUserMapLocationSelector } from '@atom/selectors/mapSelectors';
import { GeoJSON } from '@atom/types/geojson';
import {
  LocationLinkOption,
  LocationUpdatePayload,
  MapEditMetadata,
  MapEditMetadataType,
  MapParams,
} from '@atom/types/map';
import { PolicyAction } from '@atom/types/policy';
import {
  WorkOrderDetailType,
  WorkOrderUpdate,
  WorkOrderUpdateInput,
} from '@atom/types/work';
import { hasAccess } from '@atom/utilities/accessUtilities';
import api from '@atom/utilities/api';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import { isPoint } from '@atom/utilities/locationUtilities';
import {
  getGeoJsonFromMapBounds,
  getGoogleShapeCenter,
  getMapCenterAndZoomParams,
  getMapEditMetadata,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

const WORK_DETAIL_ZOOM = 17;

interface Props {
  workOrderDetail: WorkOrderDetailType;
  refetch: () => void;
  loading: boolean;
  initialOpenEditList?: boolean;
}

const WorkOrderDetailMap = ({
  workOrderDetail,
  refetch,
  loading,
  initialOpenEditList,
}: Props) => {
  const currentUserLocation = useSelector(getLoggedInUserMapLocationSelector);

  const dispatch = useGoogleMapsStateUpdater();
  const { isLoaded } = useGoogleMapsStateValue();
  const preferences = usePreferences();

  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState<string>(null);
  const [assetUpdateLoading, setAssetUpdateLoading] = useState<boolean>(false);

  const [updateWorkDetail, { loading: workUpdateLoading }] = useMutation<
    { workOrderUpdate: WorkOrderUpdate },
    { workOrder: WorkOrderUpdateInput }
  >(WORK_ORDER_UPDATE);

  useEffect(() => {
    dispatch({
      type: 'SET',
      payload: {
        key: 'grab',
        data: false,
      },
    });
  }, []);

  const {
    location: workOrderLocation,
    inventoryAssetId,
    inventoryAssetName,
    inventoryAssetLocation,
    inventoryAssetMarkerId,
    inventoryAssetAddress,
    inventoryAssetActions,
    isClosed,
  } = workOrderDetail;

  const hasAsset = !R.isEmpty(inventoryAssetId);

  const [{ center, zoom, searchPoint }, { updateSearchParams }] = useNavigation(
    {
      center: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      searchPoint: SearchParamType.STRING,
    },
  );

  useEffect(() => {
    const centerShape = getGoogleShapeCenter(workOrderLocation);
    updateSearchParams({
      zoom: WORK_DETAIL_ZOOM,
      center: `${centerShape?.lat},${centerShape?.lng}`,
    });
  }, []);

  const onLoad = useCallback(setMap, []);

  const onIdle = useCallback(() => {
    try {
      const existingMapParams: MapParams = {
        center,
        zoom,
        searchPoint,
      };

      const mapParams = getMapCenterAndZoomParams(
        map,
        existingMapParams,
        preferences,
        currentUserLocation,
      );

      if (!R.equals(existingMapParams, mapParams) || !coordinates) {
        updateSearchParams({ zoom: mapParams.zoom, center: mapParams.center });
        R.pipe(
          getGeoJsonFromMapBounds,
          R.prop('coordinates'),
          JSON.stringify,
          setCoordinates,
        )(map);
      }
    } catch {
      updateSearchParams({ zoom: null, center: null });
    }
  }, [
    map,
    center,
    zoom,
    searchPoint,
    coordinates,
    preferences,
    updateSearchParams,
  ]);

  const asset = {
    id: inventoryAssetId,
    name: inventoryAssetName,
    location: inventoryAssetLocation,
    address: inventoryAssetAddress,
    markerId: inventoryAssetMarkerId,
  };

  const canUpdateAssetLocation = hasAccess(
    PolicyAction.UPDATE_LOCATION,
    inventoryAssetActions,
  );

  const canEditAsset = !isClosed && canUpdateAssetLocation;
  const canEditWorkOrder = !isClosed && hasRolePermissions(ROLE_SETS.INSPECTOR);

  const locationLinkControlsEnabled =
    (canEditAsset || canEditWorkOrder) &&
    hasAsset &&
    isPoint(inventoryAssetLocation);

  const mapEditMetadata: MapEditMetadata[] = useMemo(
    () => [
      getMapEditMetadata(
        workOrderDetail,
        canEditWorkOrder,
        MapEditMetadataType.WORK_ORDER,
      ),
      ...(hasAsset
        ? [getMapEditMetadata(asset, canEditAsset, MapEditMetadataType.ASSET)]
        : []),
    ],
    [workOrderDetail],
  );

  const mapData = {
    workOrders: [workOrderDetail],
    assets: [
      {
        id: inventoryAssetId,
        location: inventoryAssetLocation,
        markerId: inventoryAssetMarkerId,
      },
    ],
  };

  const updateWorkLocation = async (location: GeoJSON) => {
    await updateWorkDetail({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          location,
        },
      },
    });

    refetch();
  };

  const updateAssetLocation = async (location: GeoJSON) => {
    setAssetUpdateLoading(true);

    const endpoint = `${INVENTORY_ASSETS_ENDPOINT}/${inventoryAssetId}`;
    await api.patch(endpoint, { location });

    setAssetUpdateLoading(false);
    refetch();
  };

  const onSave = (payload: LocationUpdatePayload) => {
    if (payload?.type === MapEditMetadataType.WORK_ORDER) {
      updateWorkLocation(payload?.location);
    } else {
      updateAssetLocation(payload?.location);
    }
  };

  const setInventoryAssetLocationFromWorkOrder = async () => {
    await updateAssetLocation(workOrderLocation);
  };

  const setWorkOrderLocationFromInventoryAsset = async () => {
    await updateWorkLocation(inventoryAssetLocation);
  };

  const locationLinkOptions: LocationLinkOption[] = [
    ...(canEditWorkOrder
      ? [
          {
            text: `Sync work to inventory's location`,
            onClick: setWorkOrderLocationFromInventoryAsset,
          },
        ]
      : []),
    ...(canEditAsset
      ? [
          {
            text: `Sync inventory to work's location`,
            onClick: setInventoryAssetLocationFromWorkOrder,
          },
        ]
      : []),
  ];

  const markerLoading = workUpdateLoading || assetUpdateLoading;

  return !isLoaded ? (
    <Progress style={{ height: '100%' }} />
  ) : (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      mapStyleControl
      layerControls
      mapSearchBox
      locationLinkControls
      editControls
      mapEditMetadata={mapEditMetadata}
      onSave={onSave}
      locationEditZoom={WORK_DETAIL_ZOOM}
      loading={loading}
      locationLinkOptions={locationLinkOptions}
      locationLinkControlsDisabled={!locationLinkControlsEnabled}
      initialOpenEditList={initialOpenEditList}
    >
      {!markerLoading && <MapMarkers onClick={() => {}} data={mapData} />}
      {searchPoint && <MapPinMarker center={searchPoint} />}
    </Map>
  );
};

export default withGoogleMapsState<Props>(WorkOrderDetailMap);
